import { Injectable } from '@angular/core';
import { AmorphicService } from '../amorphic/amorphic.service';
import { ParserService } from '../../../../common/angular-shared/services/parser/parser.service';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import { ValidationService } from '../validation/validation.service';
import { FormatterService } from '../formatter/formatter.service';
import { ValidationResult } from '../../interfaces/validation/ValidationInterface';
import { SubjectManager } from '../../../../common/js/subjectManager/SubjectManager';
import * as ld from 'lodash';

/**
 * FormService :
 *
 * Defines basic functionality to interact forms based off Amorphic models.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class FormService {
  /*
        Unlike Haven, Coverpath does not link occupation and employment status.
        However, triggers exist on the model that will null out occupation if
        the applicant does not list a current employer. As such, we want to turn off
        the triggers.

        @TODO - Improve implementation as part of HL-8438 and re-instate code
        that was initially merged here: https://gitlab.dev.havenlife.com/hit/haven/merge_requests/3752
     */
  private triggersToNotFire: any = {
    CAS: [
      'customer.application.primaryPolicy.insured.financials.occupation',
      'customer.application.primaryPolicy.insured.financials.employed',
      'customer.application.primaryPolicy.insured.financials.unemployed',
    ],
    MMDirect: [
      'customer.application.primaryPolicy.insured.financials.occupation',
      'customer.application.primaryPolicy.insured.financials.employed',
      'customer.application.primaryPolicy.insured.financials.unemployed',
    ],
  };

  protected controller: CoverpathControllerInterface;
  constructor(
    protected amorphicService: AmorphicService,
    protected parserService: ParserService,
    protected formatterService: FormatterService,
    protected validationService: ValidationService
  ) {
    this.controller = amorphicService.controller;
  }

  public submitValueAndFireTriggers(prop: string, value) {
    this.setValue(prop, value);
    this.fireTrigger(prop);
    this.markPropertyAsChanged(prop);
  }

  public markPropertyAsChanged(prop: string) {
    const propertyName = prop.split('.').join('_') + '_changed';
    SubjectManager.publish(propertyName);
  }

  public parse(prop, value) {
    const model = this.getModelFor(prop);
    const propString = this.getPropStringFor(prop);
    let propertyDef = model.__prop__(propString);
    if (!propertyDef) {
      propertyDef = model[propString];
    }
    const propTypeFunc = propertyDef.type;
    return this.parserService.parse(propTypeFunc, value);
  }

  public getValue(prop: string) {
    return ld.get(this.controller, prop, null);
  }

  public setValue(prop: string, value): void {
    ld.set(this.controller, prop, value);
  }

  public fireTrigger(prop: string): void {
    const modelProp = `this.controller.${prop}`;

    const channelCode = this.controller.customer.getChannelInfo().code;
    const doNotFireTrigger =
      this.triggersToNotFire[channelCode] && this.triggersToNotFire[channelCode].indexOf(prop) > -1;

    if (!doNotFireTrigger) {
      //tslint:disable-next-line
      eval('if(typeof (' + modelProp + 'Trigger) === "function"){ ' + modelProp + 'Trigger()}');
    }

    this.saveChanges();
  }

  public saveChanges() {
    this.amorphicService.save();
  }

  public format(formatterType: string, value) {
    return this.formatterService.format(formatterType, value);
  }

  public async runModelValidation(prop: string, value) {
    const model = this.getModelFor(prop);
    const propString = this.getPropStringFor(prop);

    const propertyDef = model.__prop__(propString);
    let result: ValidationResult = { result: true };

    if (propertyDef) {
      const rules = propertyDef.rule;
      let rulesArr = [];
      if (rules) {
        rulesArr = Array.isArray(rules) ? rules : [rules];
      }
      let isValid: boolean = rulesArr.every((rule) => {
        // Keep going until the first failure
        result = this.validationService.runRule(rule, value);
        return result.result;
      });

      if (isValid) {
        const validatorStr = propertyDef.validate;
        if (validatorStr) {
          const validatorStrArray: Array<any> = validatorStr.split(';');
          const promises = [];
          validatorStrArray.forEach((validator) => {
            promises.push(this.validationService.runValidator(validator, value));
          });
          isValid = (await Promise.all(promises)).every((res: ValidationResult) => {
            result = res;
            return result && result.result;
          });
        }
      }

      if (isValid && propertyDef.compositeValidators) {
        const promises = [];
        propertyDef.compositeValidators.forEach((validatorFn) => {
          promises.push(this.validationService[validatorFn](model, prop, value));
        });
        (await Promise.all(promises)).every((res: ValidationResult) => {
          result = res;
          return result && result.result;
        });
      }
    }
    return result;
  }

  public getValuesFor(property: string) {
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);
    const result = model.__values__(propString);
    const values = 'this.controller.' + property + 'Values';

    if (!result) {
      //tslint:disable-next-line
      return typeof eval(values) === 'function' ? eval(values + '()') : eval(values);
    }

    return result;
  }

  public getDescriptionsFor(property: string) {
    const model = this.getModelFor(property);
    const propString = this.getPropStringFor(property);

    const result = model.__descriptions__(propString);
    const descriptions = 'this.controller.' + property + 'Descriptions';

    if (!result) {
      //tslint:disable-next-line
      return typeof eval(descriptions) === 'function' ? eval(descriptions + '()') : eval(descriptions);
    }

    return result;
  }

  protected getModelFor(prop: string) {
    const propParts = prop.split('.');
    const modelString = propParts.slice(0, -1).join('.');

    //tslint:disable-next-line
    return modelString === '' ? eval('this.controller') : eval('this.controller.' + modelString);
  }

  protected getPropStringFor(prop: string): string {
    const propParts = prop.split('.');
    return propParts[propParts.length - 1];
  }
}
